import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiX, FiSend } from 'react-icons/fi';
import axios from 'axios';
import AlertDialog from '../Components/Shared/AlertDialog';
import { ENDPOINTS as LMS_ENDPOINTS } from '../Utils/Endpoints';
import { ENDPOINTS as SEC_ENDPOINTS } from '../../Seguridad/Utils/Endpoints';
import { Models } from '../Utils/ModelsLMS';
import { getSessionUserId } from '../../../utils/SessionUser';
import '../Styles/ObservacionesModal.css';

const ObservacionesCarreraModal = ({ isOpen, onClose, idCarrera, currentContentId, currentContentType, onObservationAdded, readOnly = false, onReferenceClick }) => {
    const navigate = useNavigate();
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [lastPeticion, setLastPeticion] = useState(null);
    const messagesEndRef = useRef(null);
    const [alertDialog, setAlertDialog] = useState({ open: false, title: "Información", message: "", variant: "info" });

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Load messages and finding the last petition
    useEffect(() => {
        if (isOpen && idCarrera) {
            loadData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, idCarrera]);

    const loadData = async (showSpinner = true) => {
        if (showSpinner) setLoading(true);
        try {
            // 1. Get all Petitions (Certificados)
            const resPeticiones = await axios.get(LMS_ENDPOINTS.peticionRevisionCarrera);
            const peticiones = resPeticiones.data || [];

            // Filter by idCarrera
            const carreraPeticiones = peticiones.filter(p =>
                (p.idCarrera || p.IdCarrera) === Number(idCarrera)
            );

            // Sort by ID descending to find the last one
            carreraPeticiones.sort((a, b) => (b.idPeticionRevisionCarrera || b.IdPeticionRevisionCarrera) - (a.idPeticionRevisionCarrera || a.IdPeticionRevisionCarrera));

            if (carreraPeticiones.length > 0) {
                setLastPeticion(carreraPeticiones[0]);
            } else {
                setLastPeticion(null);
            }

            // 2. Get Observaciones y Referencias + Catalogos + Usuarios/Personas
            const [
                resObservaciones,
                resReferencias,
                resLecciones,
                resCuestionarios,
                resExamenes,
                resModuloContenido,
                resUsuarios,
                resPersonas
            ] = await Promise.all([
                axios.get(LMS_ENDPOINTS.observacionCarrera),
                axios.get(LMS_ENDPOINTS.referencias),
                axios.get(LMS_ENDPOINTS.lecciones),
                axios.get(LMS_ENDPOINTS.cuestionario),
                axios.get(LMS_ENDPOINTS.examen),
                axios.get(LMS_ENDPOINTS.moduloContenido),
                axios.get(SEC_ENDPOINTS.usuarios),
                axios.get(SEC_ENDPOINTS.personas)
            ]);

            const observaciones = (resObservaciones.data || []).map(Models.observacionCarrera);
            const referencias = resReferencias.data || [];

            const lecciones = resLecciones.data || [];
            const cuestionarios = resCuestionarios.data || [];
            const examenes = resExamenes.data || [];
            const moduloContenido = (resModuloContenido.data || []).map(Models.moduloContenido);
            const usuarios = resUsuarios.data || [];
            const personas = resPersonas.data || [];

            // Filter observations that belong to any of the course's petitions
            const peticionIds = carreraPeticiones.map(p => p.idPeticionRevisionCarrera || p.IdPeticionRevisionCarrera);
            const courseObservaciones = observaciones.filter(o => peticionIds.includes(o.idPeticion));

            // Sort messages structure
            const chatMessages = courseObservaciones.map(o => {
                const ref = referencias.find(r => r.idObservacion === o.idObservacion);
                let refName = null;
                let refModuleId = null;

                // Resolve Admin Name logic requested:
                // Obs -> Peticion -> UsuarioAtendio -> Usuario -> Persona -> Nombre
                const obsPeticion = carreraPeticiones.find(p => (p.idPeticionRevisionCarrera || p.IdPeticionRevisionCarrera) === o.idPeticion);
                let authorName = "Desconocido";

                if (obsPeticion) {
                    const idSolicitante = obsPeticion.idUsuarioSolicitante || obsPeticion.IdUsuarioSolicitante;
                    const idAtendio = obsPeticion.idUsuarioAtendio || obsPeticion.IdUsuarioAtendio;
                    
                    // Prioridad: Si hay alguien que atendió y la observación es posterior, o si es del solicitante
                    // Pero como el sistema actual asocia observaciones a la petición general, 
                    // vamos a intentar identificar quién escribió basándonos en si la petición está "Atendida"
                    // En este sistema, las observaciones suelen ser del Administrador que revisa.
                    
                    const idAutor = idAtendio || idSolicitante;

                    if (idAutor) {
                        const usuarioAutor = usuarios.find(u => (u.idUsuario || u.IdUsuario) === idAutor);
                        if (usuarioAutor) {
                            const personaAutor = personas.find(p => (p.idPersona || p.IdPersona) === usuarioAutor.idPersona);
                            if (personaAutor) {
                                authorName = `${personaAutor.nombre || ''} ${personaAutor.apellidoPaterno || ''} ${personaAutor.apellidoMaterno || ''}`.trim();
                            } else {
                                authorName = usuarioAutor.nombreUsuario || usuarioAutor.NombreUsuario;
                            }
                        }
                    } else {
                        authorName = "Pendiente de atención";
                    }
                }

                if (ref) {
                    const idRef = ref.idReferencial;
                    const tipo = ref.tipo;

                    // Map Referencia Tipo to ModuloContenido TablaContenido
                    let tablaContenido = "";

                    if (tipo === 'LECCION') {
                        tablaContenido = "Lecciones";
                        const l = lecciones.find(x => (x.idLeccion || x.IdLeccion) === idRef);
                        refName = l ? (l.titulo || l.Titulo) : 'Lección desconocida';
                    } else if (tipo === 'CUESTIONARIO') {
                        tablaContenido = "Cuestionario";
                        const q = cuestionarios.find(x => (x.idCuestionario || x.IdCuestionario) === idRef);
                        refName = q ? (q.nombre || q.Nombre) : 'Cuestionario desconocido';
                    } else if (tipo === 'EXAMEN') {
                        tablaContenido = "Examen";
                        const e = examenes.find(x => (x.idExamen || x.IdExamen) === idRef);
                        refName = e ? (e.nombre || e.Nombre) : 'Examen desconocido';
                    } else {
                        refName = `Referencia ${tipo}`;
                    }

                    // Find moduleId using ModuloContenido
                    if (tablaContenido) {
                        const mc = moduloContenido.find(x =>
                            (x.idAsociado === idRef) &&
                            (x.tablaContenido === tablaContenido)
                        );
                        if (mc) {
                            refModuleId = mc.idModulo;
                        }
                    }
                }

                return {
                    id: o.idObservacion,
                    text: o.observacion,
                    authorName: authorName,
                    isUser: true,
                    date: new Date().toISOString(),
                    reference: ref ? {
                        tipo: ref.tipo,
                        idReferencial: ref.idReferencial,
                        name: refName,
                        moduleId: refModuleId
                    } : null
                };
            }).sort((a, b) => a.id - b.id);

            // FILTERING LOGIC
            let filteredMessages = chatMessages;
            if (currentContentId && currentContentType) {
                // We are in specific content (Player), show observations for this content + global messages
                filteredMessages = chatMessages.filter(msg =>
                    !msg.reference || (
                        msg.reference.tipo === currentContentType &&
                        msg.reference.idReferencial === Number(currentContentId)
                    )
                );
            }

            setMessages(filteredMessages);

        } catch (error) {
            console.error("Error loading observations:", error);
            setAlertDialog({
                open: true,
                title: 'Error',
                message: 'Error: No se pudieron cargar las observaciones',
                variant: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handlePublish = async () => {
        if (!newMessage.trim()) return;
        if (!lastPeticion) {
            setAlertDialog({
                open: true,
                title: 'Atención',
                message: 'No hay una petición pendiente para asociar a esta observación.',
                variant: 'warning'
            });
            return;
        }

        setSending(true); // Disable button while publishing
        try {
            const peticionId = lastPeticion.idPeticionRevisionCarrera || lastPeticion.IdPeticionRevisionCarrera;

            // 1. Save Observation
            const observacionDTO = {
                IdPeticion: peticionId,
                Observacion: newMessage,
                Activo: true
            };

            const resObs = await axios.post(LMS_ENDPOINTS.observacionCarrera, observacionDTO);

            // Extract new ID robustly
            const newObsData = resObs.data;
            const newObsId = newObsData?.idObservacion || newObsData?.IdObservacion 
                || newObsData?.id || newObsData?.Id
                || (typeof newObsData === 'number' ? newObsData : null);

            console.log("[ObservacionesCarreraModal] New Observation ID:", newObsId);
            console.log("[ObservacionesCarreraModal] Content Context:", { currentContentId, currentContentType });

            // 2. Save Reference (if context exists AND we have a valid observation ID)
            if (newObsId && currentContentId && currentContentType) {
                const referenciaDTO = {
                    IdObservacion: newObsId,
                    IdReferencial: Number(currentContentId),
                    Tipo: currentContentType,
                    Activo: true
                };
                await axios.post(LMS_ENDPOINTS.referencias, referenciaDTO);
            }

            // 2. Update Last Peticion (Mark as attended if not already)
            if (!(lastPeticion.atendido || lastPeticion.Atendido)) {
                const userId = getSessionUserId();
                const peticionUpdateDTO = {
                    IdPeticionRevisionCarrera: peticionId,
                    IdCarrera: lastPeticion.idCarrera || lastPeticion.IdCarrera,
                    IdUsuarioSolicitado: lastPeticion.idUsuarioSolicitado || lastPeticion.IdUsuarioSolicitado,
                    IdUsuarioSolicitante: lastPeticion.idUsuarioSolicitante || lastPeticion.IdUsuarioSolicitante,
                    FechaPeticion: lastPeticion.fechaPeticion || lastPeticion.FechaPeticion,
                    Atendido: true,
                    IdUsuarioAtendio: userId ? Number(userId) : null,
                    Activo: lastPeticion.activo !== undefined ? lastPeticion.activo : (lastPeticion.Activo !== undefined ? lastPeticion.Activo : true)
                };

                await axios.put(LMS_ENDPOINTS.peticionRevisionCarrera, peticionUpdateDTO);
                console.log("[ObservacionesCarreraModal] Petition marked as attended.");
            }

            // UI Updates
            setNewMessage("");


            // Reload to show the new message (without spinner)
            await loadData(false);
            if (onObservationAdded) onObservationAdded();

        } catch (error) {
            console.error("Error publishing observation:", error);
            setAlertDialog({
                open: true,
                title: 'Error',
                message: 'Ocurrió un error al publicar la observación',
                variant: 'error'
            });
        } finally {
            setSending(false);
        }
    };

    const handleReferenceClick = (ref) => {
        if (!ref || !ref.moduleId) return;

        // Custom navigation if provided
        if (onReferenceClick) {
            onClose();
            onReferenceClick(ref);
            return;
        }

        // Default navigation (Player)
        onClose(); // Close modal
        navigate(`/menu/lms/carrera-player-revision/${idCarrera}/${ref.moduleId}/${ref.idReferencial}/${ref.tipo}`);
    };

    if (!isOpen) return null;

    return (
        <div className="observaciones-modal-overlay">
            <div className="observaciones-modal-content">
                <div className="observaciones-modal-header">
                    <h3>Historial de Observaciones</h3>
                    <button className="observaciones-close-btn" onClick={onClose}>
                        <FiX />
                    </button>
                </div>

                <div className="observaciones-modal-body">
                    {loading ? (
                        <div className="observaciones-loading">Cargando...</div>
                    ) : (
                        <div className="observaciones-chat-area">
                            {messages.length === 0 ? (
                                <p className="observaciones-empty">No hay observaciones registradas.</p>
                            ) : (
                                messages.map((msg) => (
                                    <div key={msg.id} className="observaciones-message">
                                        <div className="observaciones-message-content">
                                            {msg.reference && (
                                                <div
                                                    className="observaciones-reference-header"
                                                    onClick={() => handleReferenceClick(msg.reference)}
                                                    title={msg.reference.moduleId ? "Ir al contenido" : ""}
                                                >
                                                    {msg.reference.tipo === 'LECCION' ? 'Lección' :
                                                        msg.reference.tipo === 'CUESTIONARIO' ? 'Cuestionario' :
                                                            msg.reference.tipo === 'EXAMEN' ? 'Examen' :
                                                                msg.reference.tipo}: {msg.reference.name} ➚
                                                </div>
                                            )}
                                            <div className={`observaciones-bubble ${msg.reference ? 'has-reference' : ''}`}>
                                                <div className="observaciones-author">
                                                    {msg.authorName}
                                                </div>
                                                {msg.text}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                            <div ref={messagesEndRef} />
                        </div>
                    )}
                </div>

                {!readOnly && (
                    <div className="observaciones-modal-footer">
                        <div className="observaciones-input-group">
                            <textarea
                                placeholder="Escribe tu observación..."
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handlePublish();
                                    }
                                }}
                                rows={1}
                                className="observaciones-textarea"
                            />
                            <button
                                className="observaciones-send-btn"
                                onClick={handlePublish}
                                disabled={!lastPeticion || sending}
                                title={!lastPeticion ? "No hay petición pendiente" : "Publicar y Resolver"}
                            >
                                <FiSend />
                            </button>
                        </div>
                        {!lastPeticion && (
                            <small className="observaciones-warning">* No hay petición activa para comentar.</small>
                        )}
                    </div>
                )}
            </div>

            {/* Modal de alertas estándar LMS */}
            <AlertDialog
                open={alertDialog.open}
                title={alertDialog.title}
                message={alertDialog.message}
                variant={alertDialog.variant}
                onClose={() => setAlertDialog({ ...alertDialog, open: false })}
                buttonText="Aceptar"
            />
        </div>
    );
};

export default ObservacionesCarreraModal;



